<template>
  <div>
    <Spinner v-if="loading" :text="$t('payment.loading')" />

    <div id="adyen-dropin" ref="dropin"></div>

    <div class="has-text-centered my-5" v-if="completed">
      <a v-if="successful" :href="completeUrl" @click="track({ event: 'payment_success_continue' })" class="button mb-5 is-medium is-success is-blue">
        {{ $t('common.continue') }}
      </a>
      <div v-else>
        <button @click="chooseAnotherPaymentMethod" class="button is-block m-auto mb-5 is-medium is-warning">
          {{ $t('payment.another_method') }}
        </button>

        <a :href="returnUrl" @click="track({ event: 'payment_fail_continue' })" class="button mb-5 is-light is-blue">
          {{ $t('common.continue') }}
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import vars from '@/vars'
import {functions} from '@/firebase';
import Spinner from "@/components/Spinner";

const GOOGLEPAY_JS_URL = 'https://pay.google.com/gp/p/js/pay.js';
const ADYEN_JS_RELEASE = '3.18.2';
const ADYEN_CSS_RELEASE = '3.18.2';
const ADYEN_ROOT = 'https://checkoutshopper-test.adyen.com/checkoutshopper/sdk';
const ADYEN_CSS_URL = `${ADYEN_ROOT}/${ADYEN_CSS_RELEASE}/adyen.css`;
const ADYEN_JS_URL = `${ADYEN_ROOT}/${ADYEN_JS_RELEASE}/adyen.js`;

const RESULT_SUCCESS = 'success';
const RESULT_ERROR = 'error';

export default {
  components: { Spinner },
  props: [
    'secrets',
    'paymentMethods',
    'userCountry',
    'checkoutId',
    'plan',
    'returnUrl',
    'completeUrl',
    'locale'
  ],
  data() {
    return {
      loading: true,
      dropin: null,
      completed: false,
      successful: false,
      currencies: vars.CURRENCIES,
      locales: vars.LOCALES,
      usedPaymentMethod: null,
      isAndroid: navigator.userAgent.includes('Android')
    }
  },
  computed: {
    buildPlanAmount() {
      return {
        currency: this.plan.amount.currency,
        value: this.plan.amount.total
      };
    },
    formattedPrice() {
      return this.currencies[this.buildPlanAmount.currency] + (this.buildPlanAmount.value / 100)
    },
    buildPaymentConfigurations() {
      const config = {
        paypal: {
          merchantId: this.secrets.paypalMerchantId,
          environment: this.secrets.adyenEnvironment,
          countryCode: this.userCountry,
          amount: this.buildPlanAmount,
          intent: 'authorize',
          onCancel: (data, dropin) => {
            dropin.setStatus('ready');
          },
          style: {
            color: 'blue'
          }
        },
        applepay: {
          amount: this.buildPlanAmount.value,
          currencyCode: this.buildPlanAmount.currency,
          countryCode: this.userCountry,
          onSubmit: async (state) => await this.createPayment(state, this.dropin),
          onValidateMerchant: (resolve, reject, validationURL) => {
            functions.httpsCallable('appleMerchantSession')({ validationURL })
              .then(({ data }) => resolve(data))
              .catch(error => reject(error));
          },
        },
        card: {
          hasHolderName: true,
          holderNameRequired: true
        },
        paywithgoogle: {
          environment: this.secrets.adyenEnvironment === 'LIVE' ? 'PRODUCTION' : 'TEST',
          amount: this.buildPlanAmount,
          configuration: {
            gatewayMerchantId: this.secrets.adyenMerchantAccount,
            merchantName: "Premium IQ" // Optional. The name that appears in the payment sheet.
          },
          buttonColor: "white",
          onSubmit: async (state) => await this.createPayment(state, this.dropin)
        }
      };

      if (config.paywithgoogle.environment === 'PRODUCTION') {
        config.paywithgoogle.configuration.merchantIdentifier = this.secrets.googlepayMerchantId
      }

      return config;
    }
  },
  methods: {
    orderPaymentMethods() {
      const payPal = this.paymentMethods.paymentMethods.find(({ type }) => type === 'paypal')
      const card = this.paymentMethods.paymentMethods.find(({ type }) => type === 'scheme')
      const applePay = this.paymentMethods.paymentMethods.find(({ type }) => type === 'applepay')
      const googlePay = this.paymentMethods.paymentMethods.find(({ type }) => type === 'paywithgoogle')

      this.paymentMethods.paymentMethods = this.isAndroid
        ? [googlePay, applePay, payPal, card]
        : [applePay, payPal, googlePay, card]
    },
    getPaymentName(name) {
      const method = name.split('-')[0];

      switch (method) {
        case 'scheme':
          return 'card';
        case 'paywithgoogle':
          return 'googlepay';
        default:
          return method
      }
    },
    initAdyenCheckout() {
      this.orderPaymentMethods()

      const configuration = {
        locale: this.locales[this.locale], // The shopper's locale. For a list of supported locales, see https://docs.adyen.com/checkout/components-web/localization-components.
        environment: this.secrets.adyenEnvironment, // When you're ready to accept live payments, change the value to one of our live environments https://docs.adyen.com/checkout/drop-in-web#testing-your-integration.
        originKey: this.secrets.adyenOriginKey, // Your website's Origin Key. To find out how to generate one, see https://docs.adyen.com/user-management/how-to-get-an-origin-key.
        paymentMethodsResponse: this.paymentMethods, // The payment methods response returned in step 1.
        amount: this.buildPlanAmount,
        showPayButton: true,
        paymentMethodsConfiguration: this.buildPaymentConfigurations,
        onSelect: component => this.track({ event: 'paymentChoice', paymentOption: this.getPaymentName(component._id) }),
        onSubmit: this.createPayment,
        onAdditionalDetails: async (state) => {
          this.dropin.setStatus('loading');

          const response = await functions.httpsCallable('processPayment')({
            checkoutId: this.checkoutId,
            state: state
          });

          if (response.data.action) {
            return this.dropin.handleAction(response.data.action);
          }

          const result = ['Authorised', 'Received'].includes(response.data.resultCode)
            ? RESULT_SUCCESS
            : RESULT_ERROR;

          this.handlePaymentResult(this.dropin, result);
        }
      };

      const checkout = new window.AdyenCheckout(configuration);
      this.dropin = checkout.create('dropin').mount(this.$refs.dropin);
      this.loading = false;
    },
    handlePaymentResult(dropin, result) {
      this.dropin.setStatus(result, {message: this.$t(`payment.result.${result}`)});
      this.successful = result === RESULT_SUCCESS;
      this.track({ purchase: this.successful ? 'success' : 'failure' })
      this.successful && this.trackEcommerce()
      this.completed = true;
    },
    async createPayment(state, dropin) {
      this.usedPaymentMethod = this.getPaymentName(state.data.paymentMethod.type)

      this.track({ event: 'paymentProceed', paymentOption: this.usedPaymentMethod })

      dropin.setStatus('loading');

      const response = await functions.httpsCallable('createPayment')({
        checkoutId: this.checkoutId,
        payFrontUrl: window.location.href,
        state
      });

      if (response.data.action) {
        return dropin.handleAction(response.data.action);
      }

      const result = response.data.resultCode === 'Authorised'
        ? RESULT_SUCCESS
        : RESULT_ERROR;

      this.handlePaymentResult(dropin, result);
    },
    chooseAnotherPaymentMethod() {
      this.track({ event: 'choose_another_payment_method' });
      location.reload()
    },
    track(data) {
      window.dataLayer.push({ ...data, ...{ price: this.formattedPrice }})
    },
    trackEcommerce() {
      if (!vars.IS_LIVE) {
        return;
      }

      window.ga('require', 'ecommerce');

      window.ga('ecommerce:addTransaction', {
        'id': this.checkoutId,
        'affiliation': this.usedPaymentMethod,
        'revenue': (this.buildPlanAmount.value / 100).toString(),
        'currency': this.buildPlanAmount.currency
      });

      window.ga('ecommerce:addItem', {
        'id': this.checkoutId,
        'name': this.plan.id,
        'price': (this.buildPlanAmount.value / 100).toString(),
        'currency': this.buildPlanAmount.currency,
        'quantity': '1'
      });

      window.ga('ecommerce:send');
    },
  },
  mounted() {
    const loadAdyenAssets = () => {
      const adyenLink = document.createElement('link');
      adyenLink.rel = 'stylesheet';
      adyenLink.href = ADYEN_CSS_URL;
      document.head.appendChild(adyenLink);

      const adyenScript = document.createElement('script');
      adyenScript.src = ADYEN_JS_URL;
      adyenScript.async = true;
      adyenScript.onload = this.initAdyenCheckout;
      document.body.appendChild(adyenScript);
    }

    const googleScript = document.createElement('script');
    googleScript.src = GOOGLEPAY_JS_URL;
    googleScript.async = true;
    googleScript.onload = loadAdyenAssets
    document.body.appendChild(googleScript);
  },
}
</script>

<style lang="scss">
#adyen-dropin {
  ul {
    margin: 0;
  }
}
</style>
